/* ==========================================================================
   LOYAL - T3 - HOMEPAGE BEHAVIOUR
   Owner T3. Deferred, no dependencies, no library, no framework.

   THE NO JS FLOOR IS NOT A DEGRADED PAGE, IT IS THE PAGE.
   Everything below only REFINES markup the server already rendered correctly:
     - the countdown is computed in Liquid at request time, so a visitor with no
       JS sees real remaining time and the correct zero state, not dashes
     - the marquee animates in CSS, so it runs with JS off and stops dead under
       prefers-reduced-motion
     - .loyal-reveal is visible by default per TOKEN-CONTRACT section 8. This
       file opts elements IN to hiding. If it never runs, nothing is ever hidden
     - the stretch band is an enhancement on a real button and the page reads
       identically without it
   If this file 404s, the homepage still renders complete and still converts.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     1 - THE COUNTDOWN

     DRIVEN FROM AN ABSOLUTE UTC INSTANT, NEVER A LOCAL DATE STRING.
     Decision 52 supplies 2026-08-01T15:00:00Z, which is 11:00am EST. A local
     date string would be parsed in the visitor's own zone, so every timezone
     would see a different number and most of them would be wrong.

     IT NEVER COUNTS NEGATIVE AND IT NEVER LEAVES A STATIC DATE LOCKUP.
     At zero it changes state, and which state depends on the launch posture,
     which the SERVER decided and wrote onto the element. This script never
     reads settings.launch_posture and never decides posture itself.

       transact at zero  the page flips to buy
       door     at zero  the page flips to OPEN, meaning the drop is live, the
                         brand is real and the founding hundred is claimable

     A waitlist is never the payoff of a countdown, which is why there is no
     third branch here that reveals a notify form at zero.
     ---------------------------------------------------------------------- */
  function Countdown(root) {
    var iso = root.getAttribute('data-loyal-deadline');
    var deadline = Date.parse(iso);
    if (!iso || isNaN(deadline)) {
      // DEFAULT DENY. An unparseable instant must not produce a wrong number,
      // and it must not blank the block either. Leave the server's markup
      // exactly as rendered and say so where a developer will see it.
      if (window.console) {
        console.warn('[loyal] countdown deadline missing or unparseable:', iso);
      }
      return;
    }

    var slots = {
      days:    root.querySelector('[data-loyal-count="days"]'),
      hours:   root.querySelector('[data-loyal-count="hours"]'),
      minutes: root.querySelector('[data-loyal-count="minutes"]'),
      seconds: root.querySelector('[data-loyal-count="seconds"]')
    };
    var live = root.querySelector('[data-loyal-count-live]');
    var timer = null;
    var lastLiveMinute = -1;

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function open() {
      root.setAttribute('data-loyal-state', 'open');
      if (timer) { clearInterval(timer); timer = null; }
      if (live) { live.textContent = root.getAttribute('data-loyal-open-text') || ''; }
    }

    function tick() {
      var left = deadline - Date.now();
      if (left <= 0) { open(); return; }        // clamped. Never negative.

      var s = Math.floor(left / 1000);
      var d = Math.floor(s / 86400); s -= d * 86400;
      var h = Math.floor(s / 3600);  s -= h * 3600;
      var m = Math.floor(s / 60);    s -= m * 60;

      if (slots.days)    slots.days.textContent = String(d);
      if (slots.hours)   slots.hours.textContent = pad(h);
      if (slots.minutes) slots.minutes.textContent = pad(m);
      if (slots.seconds) slots.seconds.textContent = pad(s);

      // A screen reader must not be read a new number every second. The polite
      // region is refreshed once a minute and carries whole words.
      if (live && m !== lastLiveMinute) {
        lastLiveMinute = m;
        live.textContent = d + ' days ' + h + ' hours ' + m + ' minutes';
      }

      root.setAttribute('data-loyal-state', 'running');
    }

    tick();
    if (root.getAttribute('data-loyal-state') !== 'open') {
      timer = setInterval(tick, 1000);
    }

    // A phone that slept for an hour must not resume an hour behind.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) tick();
    });
  }

  /* ------------------------------------------------------------------------
     2 - THE DRAG AND SNAP BAND
     Replaces the killed roll comparison. One band, drag it, it springs back on
     T1's return spring.

     THE KEYBOARD PATH IS REQUIRED, not a courtesy, and it is the same
     mechanism rather than a parallel one: arrows stretch, release snaps back.
     The control is a real <button>, so focus, Enter and Space already work and
     nothing here reimplements them.
     ---------------------------------------------------------------------- */
  function StretchBand(el) {
    if (REDUCED) {
      // Under prefers-reduced-motion this is inert by design. It is decorative
      // and it is the one thing on the page that moves under the pointer.
      el.setAttribute('aria-disabled', 'true');
      return;
    }

    var MAX = 1.34;
    var startX = 0;
    var dragging = false;
    var scale = 1;

    function apply(next, springBack) {
      scale = Math.max(1, Math.min(MAX, next));
      el.style.transition = springBack
        ? 'transform var(--loyal-dur-return) var(--loyal-ease-return)'
        : 'none';
      el.style.transform = 'scaleX(' + scale + ')';
    }

    function release() {
      dragging = false;
      apply(1, true);
    }

    el.addEventListener('pointerdown', function (e) {
      dragging = true;
      startX = e.clientX;
      if (el.setPointerCapture) el.setPointerCapture(e.pointerId);
    });

    el.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (dx <= 0) { apply(1, false); return; }
      apply(1 + dx / (el.offsetWidth || 1), false);
    });

    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('lostpointercapture', release);
    el.addEventListener('blur', release);

    el.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowRight' || k === 'ArrowUp') {
        e.preventDefault(); apply(scale + 0.06, false);
      } else if (k === 'ArrowLeft' || k === 'ArrowDown') {
        e.preventDefault(); apply(scale - 0.06, false);
      }
    });

    el.addEventListener('keyup', function (e) {
      if (e.key.indexOf('Arrow') === 0) release();
    });
  }

  /* ------------------------------------------------------------------------
     3 - THE SCROLL REVEAL

     Against T1's attribute contract, TOKEN-CONTRACT section 8. .loyal-reveal is
     VISIBLE by default and this opts elements in to being hidden, so a visitor
     with no JS, a failed request or an old browser is never left with an empty
     page. Under prefers-reduced-motion nothing is opted in at all.

     THE BUG THIS VERSION FIXES, because it cost the only conversion event on
     the site and it is the exact failure a reader should be warned about.
     The previous version opted EVERY .loyal-reveal into opacity 0 at boot and
     unhid it only on an IntersectionObserver callback. The hero CTA is an
     anchor jump to the capture, which moves the viewport 2,288px in one frame.
     A synthetic instant jump does not reliably drive an observer that was
     armed a moment earlier on a document whose layout was still settling, so
     twelve elements below the hero, INCLUDING THE EMAIL CAPTURE, stayed at
     opacity 0 for the rest of the session. The page looked empty at the exact
     moment the visitor had asked to be sold to.

     So the observer is now a REFINEMENT and not the mechanism. The mechanism
     is `sweep`, which reveals anything whose top has reached the bottom of the
     current viewport, and it runs:
       - at boot, so everything above the initial fold is visible immediately
       - on hashchange, which is what an in page CTA produces
       - on scrollend, and on a throttled scroll where scrollend is absent
       - on load and pageshow, which covers a restored scroll position and a
         back forward cache restore
     Anything the sweep misses the observer still catches, and anything the
     observer misses the sweep still catches. Neither is load bearing alone.
     ---------------------------------------------------------------------- */
  var sweepReveals = function () {};

  function reveals() {
    if (REDUCED || !('IntersectionObserver' in window)) return;
    var nodes = Array.prototype.slice.call(
      document.querySelectorAll('.loyal-reveal'));
    if (!nodes.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) show(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });

    function show(n) {
      n.setAttribute('data-loyal-reveal', 'in');
      io.unobserve(n);
    }

    // Reveal anything that has reached the bottom of the CURRENT viewport, and
    // drop it from the list so this stays cheap however often it is called.
    function sweep() {
      for (var i = nodes.length - 1; i >= 0; i--) {
        var n = nodes[i];
        if (n.getAttribute('data-loyal-reveal') === 'in') {
          nodes.splice(i, 1);
          continue;
        }
        if (n.getBoundingClientRect().top < window.innerHeight) {
          show(n);
          nodes.splice(i, 1);
        }
      }
    }
    sweepReveals = sweep;

    nodes.forEach(function (n) {
      n.setAttribute('data-loyal-reveal', 'pending');
      io.observe(n);
    });

    // BOOT SWEEP. Everything above the initial fold is revealed before a single
    // frame is painted hidden, so the first screen never flickers and never
    // depends on an observer callback that may arrive after the jump.
    sweep();

    var raf = window.requestAnimationFrame || function (f) { return setTimeout(f, 16); };
    function deferredSweep() { raf(function () { raf(sweep); }); }

    window.addEventListener('hashchange', deferredSweep);
    window.addEventListener('load', deferredSweep);
    window.addEventListener('pageshow', deferredSweep);
    window.addEventListener('resize', deferredSweep);

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', sweep);
    } else {
      var t = null;
      window.addEventListener('scroll', function () {
        if (t) clearTimeout(t);
        t = setTimeout(sweep, 120);
      }, { passive: true });
    }
  }

  /* ------------------------------------------------------------------------
     4 - THE IN PAGE JUMP

     A bare `href="#loyal-capture"` scrolls the page and LEAVES FOCUS ON BODY.
     A keyboard visitor who activates the site's only call to action is put
     back at the top of the tab order and has to traverse the whole header and
     hero again to reach the field they just asked for. That is not a polish
     item on a page whose one job is a single email address.

     So the anchor keeps its href, which is what a visitor with no JS uses and
     what a middle click and a right click copy must still see, and this only
     upgrades the click: reveal the destination, scroll to it honouring
     prefers-reduced-motion, then move focus to the field itself.

     `preventScroll: true` matters. Focusing an input inside a smooth scroll
     without it makes the browser jump instantly to the field and cancels the
     animation, which is the exact abrupt movement reduced motion exists to
     avoid for everyone else.
     ---------------------------------------------------------------------- */
  function Jump(a) {
    // The theme editor calls boot() again on every section re render, so a
    // second listener would fire the same scroll and focus twice.
    if (a.getAttribute('data-loyal-bound') === 'jump') return;
    a.setAttribute('data-loyal-bound', 'jump');

    a.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey ||
          e.altKey || e.button !== 0) return;

      var hash = a.getAttribute('href') || '';
      if (hash.charAt(0) !== '#' || hash.length < 2) return;

      var landing = document.getElementById(hash.slice(1));
      if (!landing) return;   // default deny: let the browser do whatever it does

      e.preventDefault();

      // The destination must not be invisible when the visitor arrives.
      sweepReveals();

      var focusSel = a.getAttribute('data-loyal-focus');
      var target = focusSel ? document.querySelector(focusSel) : null;

      landing.scrollIntoView({
        behavior: REDUCED ? 'auto' : 'smooth',
        block: 'start'
      });

      if (window.history && history.replaceState) {
        history.replaceState(null, '', hash);
      }

      if (!target) {
        target = landing;
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      }
      try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }

      // The jump moved the viewport a long way in one frame. Sweep again after
      // the browser has settled, which is the case the old code got wrong.
      var raf = window.requestAnimationFrame || function (f) { return setTimeout(f, 16); };
      raf(function () { raf(sweepReveals); });
      setTimeout(sweepReveals, 400);
    });
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-loyal-countdown]'), Countdown);
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-loyal-stretch]'), StretchBand);
    reveals();
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-loyal-jump]'), Jump);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
